import React, { useState } from 'react';

/**
 * Displays Long-Term Care (LTSS) breakdown when pipeline data is available.
 * Shows outlays (HCBS, NF), copay receipts, and Member Income contribution.
 */
export default function LtssInfoCard({ yearlyResults, config }) {
    const [selectedYear, setSelectedYear] = useState(config?.startYear ?? 2027);
    const [isExpanded, setIsExpanded] = useState(true);

    if (!yearlyResults?.length) return null;

    const yearData = yearlyResults.find(d => d.year === selectedYear);
    if (!yearData) return null;

    const hasLtssData = yearData.costLTSS != null && yearData.costLTSS > 0;
    const hasPipelineDetail = yearData.ltssOutlaysHcbsBillions != null || yearData.ltssOutlaysNfBillions != null;

    const fmt = (val, decimals = 1) => (val != null && !Number.isNaN(val)) ? `$${Number(val).toFixed(decimals)}B` : '–';

    return (
        <div className="ltss-info-card">
            <div className="ltss-card-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h3><span className="control-group-icon">🛏️</span> Long-Term Care (LTSS)</h3>
                <span className="ltss-toggle">{isExpanded ? '−' : '+'}</span>
            </div>
            {isExpanded && (
                <div className="ltss-card-content">
                    <div className="ltss-year-select">
                        <label>Year:</label>
                        <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                            {yearlyResults.filter(d => d.year >= config?.startYear).map(d => (
                                <option key={d.year} value={d.year}>{d.year}</option>
                            ))}
                        </select>
                    </div>
                    {hasLtssData ? (
                        <table className="ltss-table">
                            <tbody>
                                <tr>
                                    <td>LTSS Outlays</td>
                                    <td className="ltss-value">{fmt((yearData.costLTSS || 0) / 1e9)}</td>
                                </tr>
                                {hasPipelineDetail && (
                                    <>
                                        <tr>
                                            <td className="ltss-sub">HCBS</td>
                                            <td className="ltss-value">{fmt(yearData.ltssOutlaysHcbsBillions ?? 0)}</td>
                                        </tr>
                                        <tr>
                                            <td className="ltss-sub">Nursing Facility</td>
                                            <td className="ltss-value">{fmt(yearData.ltssOutlaysNfBillions ?? 0)}</td>
                                        </tr>
                                    </>
                                )}
                                <tr>
                                    <td>Copay Receipts (§204(g))</td>
                                    <td className="ltss-value ltss-inflow">{fmt(yearData.ltssCopayReceiptsBillions ?? 0)}</td>
                                </tr>
                                <tr>
                                    <td>Member Income (§104(a)(13)(K))</td>
                                    <td className="ltss-value ltss-inflow">{fmt(yearData.ltssMiContribBillions ?? 0)}</td>
                                </tr>
                            </tbody>
                        </table>
                    ) : (
                        <p className="ltss-no-data">No LTSS data for this year. LTSS appears when using pipeline data and enrollment has phased in.</p>
                    )}
                </div>
            )}
        </div>
    );
}
